import { useEffect, useState } from "react";
import OffCanvas from "../../components/offCanvas";
import AddPatient from "./AddPatient";
import {
  ColDef,
  RowClickedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { Link, useNavigate } from "react-router-dom";
import React from "react";
import Modal from "react-bootstrap/Modal";
import { GenericGrid } from "../../grid/GenericGrid";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import {
  FilterCriteria,
  GridDataRequest,
  GridDataResponse,
} from "patients-experience-client";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { GridWorkflowStatusDropdown } from "../../grid/GridWorkflowStatusDropdown";
import {
  PathwaysConstants,
  ProgramConstants,
  WorkflowConstants,
} from "../../helper/WorkflowConstants";
import { WorkflowState } from "common-util-experience-client";
import { formatDate } from "../../helper/dateFormatter";
import { GridUsersDropdown } from "../../grid/GridUsersDropdown";
import { UsersDto } from "users-experience-client";
import { GridDropdown } from "../../grid/GridDropdown";
import { Lookup } from "../../interfaces/allergy";
import { GridLookupDropdown } from "../../grid/GridLookupDropdown";
import Lookups, { Constants } from "../../constants/Lookups";
import { formatDateTimeToLocal } from "../schedule/dateFormat";
import { useDispatch, useSelector } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { ReactComponent as ActiveFlag } from "../../assets/img/ActiveFlag.svg";
import { ReactComponent as InactiveFlag } from "../../assets/img/InactiveFlag.svg";
import { UserContextHolder } from "../../auth/UserContextHolder";
import {
  createCommonUtilsApi,
  createWorkflowApi,
} from "../../api-helpers/CommonUtilsApiHelper";
import ExportDataButton from "../../grid/ExportDataButton";
import { RootState } from "../../store";
import { resetFilter } from "../../store/slices/filterSlice";
import { useRoleAuthorization } from "../../customHooks/useRoleAuthorization";
import { USER_ROLES } from "../../constants/UserRoles";
import { BooleanIconRenderer } from "../../grid/renderers/BooleanIconRenderer";
import PhoneCellRenderer from "../../renderers/PhoneCellRenderer";

interface FlaggedOption {
  value: string;
  label: string;
}

interface UpcomingApptOption {
  value: string;
  label: string;
}

export const Patients = () => {
  const navigate = useNavigate();

  const patientsApi = createPatientApi();

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const workflowApi = createWorkflowApi();
  const [pathwayStatuses, setPathwayStatuses] = useState<WorkflowState[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [statuses, setStatuses] = useState<WorkflowState[]>([]);
  const [initialStatusIds, setInitialStatusIds] = useState<string[]>([]);
  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [offCanvasTitle, setOffCanvasTitle] = useState("");
  const [modalShow, setModalShow] = React.useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [program, setProgram] = useState<Lookup[]>([]);
  const [flagged, setFlagged] = useState<FlaggedOption[]>([]);
  const [upcomingAppts, setUpcomingAppts] = useState<UpcomingApptOption[]>([]);
  const [initialUpcomingAppts, setInitialUpcomingAppts] = useState<string[]>(
    [],
  );

  const lookupApi = createCommonUtilsApi();

  const [searchText, setSearchText] = useState<string>();
  const [coaches, setCoaches] = useState<string[] | null>();
  const [currentUser, setCurrentUser] = useState<UsersDto>();
  const isClinicalLiaison = useRoleAuthorization([USER_ROLES.CLINICAL_LIAISON]);
  const showExportButton = useRoleAuthorization([
    USER_ROLES.ADMINISTRATOR,
    USER_ROLES.SUPERVISOR,
  ]);

  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const pageFilterState = useSelector((state: RootState) => state.pageFilter);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setPageTitle("Patients | Spire 360"));
    UserContextHolder.addListener(() => {
      setCurrentUser(UserContextHolder.getUser());
    });
  }, []);

  useEffect(() => {
    if (currentUser && isClinicalLiaison) {
      setCoaches([currentUser.id]);
    }
  }, [currentUser]);

  const updateFilters = () => {
    const filters: FilterCriteria[] = [];
    const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (searchText && datePattern.test(searchText)) {
      filters.push({
        propertyName: "patientDateOfBirth",
        operator: "equals",
        propertyValue: searchText,
      });
    } else if (searchText?.length) {
      filters.push({
        propertyName: "patientFullName",
        operator: "contains",
        propertyValue: searchText,
      });
      filters.push({
        propertyName: "wellinksId",
        operator: "contains",
        propertyValue: searchText,
      });

      const phoneNumber = /^\(\d{3}\)\s\d{3}-\d{4}$/.test(searchText || "")
        ? searchText.replace(/[^0-9]/g, "")
        : searchText;
      if (phoneNumber.length > 0) {
        filters.push({
          propertyName: "phone",
          operator: "contains",
          propertyValue: phoneNumber,
        });
      }
    }
    if (coaches && coaches.length > 0) {
      const includeNull = coaches.some((coach) => coach === Constants.NOT_SET);
      const filterOutIsNotSetCoach = coaches.filter(
        (coach) => coach !== Constants.NOT_SET,
      );

      if (filterOutIsNotSetCoach.length > 0 && includeNull) {
        filters.push({
          propertyName: "primaryCoachId",
          operator: "in-or-null",
          propertyValue: [...filterOutIsNotSetCoach, null],
        });
      } else if (filterOutIsNotSetCoach.length > 0) {
        filters.push({
          propertyName: "primaryCoachId",
          operator: "in",
          propertyValue: filterOutIsNotSetCoach,
        });
      } else if (includeNull) {
        filters.push({
          propertyName: "primaryCoachId",
          operator: "is-null",
          propertyValue: null,
        });
      }
    }
    if (statuses && statuses.length > 0) {
      const includeNull = statuses.some(
        (status) => status.stateId === Constants.NOT_SET.toLowerCase(),
      );
      const pathwayValues = statuses
        .filter((status) => status.stateId !== Constants.NOT_SET.toLowerCase())
        .map((status) => status.stateId);
      if (pathwayValues.length > 0 && includeNull) {
        filters.push({
          propertyName: "pathwayWfStateId",
          operator: "in-or-null",
          propertyValue: [...pathwayValues, null],
        });
      } else if (pathwayValues.length > 0) {
        filters.push({
          propertyName: "pathwayWfStateId",
          operator: "in",
          propertyValue: pathwayValues,
        });
      } else if (includeNull) {
        filters.push({
          propertyName: "pathwayWfStateId",
          operator: "is-null",
          propertyValue: null,
        });
      }
    }
    if (program && program.length > 0) {
      filters.push({
        propertyName: "patientProgram",
        operator: "in",
        propertyValue: program.map((item) => item.name),
      });
    }
    if (flagged && flagged.length > 0) {
      filters.push({
        propertyName: "isCensus",
        operator: "in",
        propertyValue: flagged.map((p) => p.value),
      });
    }
    if (upcomingAppts && upcomingAppts.length > 0 && upcomingAppts.length < 2) {
      upcomingAppts.map((fltr) => {
        if (fltr.value === Constants.UPCOMING_APPTS) {
          filters.push({
            propertyName: "nextAppointment",
            operator: "is-not-null",
            propertyValue: "",
          });
        } else if (fltr.value === Constants.NO_UPCOMING_APPTS) {
          filters.push({
            propertyName: "nextAppointment",
            operator: "is-null",
            propertyValue: null,
          });
        }
      });
    }
    setFilters(filters);
  };

  useEffect(updateFilters, [
    statuses,
    searchText,
    coaches,
    program,
    flagged,
    upcomingAppts,
  ]);


  const FlagRenderer = (params: ValueGetterParams) => {
    const flagged = params.data[params.colDef.field!];
    const flag = flagged ? <ActiveFlag /> : <InactiveFlag />;
    return flag;
  };

  const colDefs: ColDef[] = [
    {
      field: "isCensus",
      headerName: "",
      width: 30,
      minWidth: 30,
      pinned: "left",
      cellClass: "cursor-pointer",
      sortable: true,
      sort: "desc",
      cellRenderer: FlagRenderer,
    },
    {
      field: "patientFirstName",
      headerName: "First Name",
      width: 150,
      minWidth: 120,
      pinned: "left",
      sortable: true,
      sort: "asc",
      cellClass: "cursor-pointer",
    },
    {
      field: "patientLastName",
      headerName: "Last Name",
      width: 150,
      minWidth: 120,
      pinned: "left",
      sortable: true,
      cellClass: "cursor-pointer",
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 170,
      minWidth: 150,
      pinned: "left",
      cellRenderer: PhoneCellRenderer,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "wellinksId",
      headerName: "Wellinks ID",
      minWidth: 140,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "currentMonthInteractiveActivities",
      headerName: "Interactive Activities",
      headerTooltip:
        "Displays whether the patient has interactive activities for the current month (updated hourly)",
      width: 175,
      sortable: true,
      cellRenderer: BooleanIconRenderer,
    },
    {
      field: "nextAppointment",
      headerName: "Next Appointment",
      width: 200,
      minWidth: 150,
      valueFormatter: (params) =>
        formatDateTimeToLocal(params.data.nextAppointment),
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "lastAppointment",
      headerName: "Last Appointment",
      width: 200,
      minWidth: 150,
      valueFormatter: (params) =>
        formatDateTimeToLocal(params.data.lastAppointment),
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "pathwayStateName",
      headerName: "Pathway",
      width: 150,
      minWidth: 150,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "riskScore",
      headerName: "Risk Strat Score",
      width: 170,
      minWidth: 120,
      valueFormatter: (params: ValueFormatterParams) =>
        params.data.riskScore ?? "-",
      sortable: true,
      cellClass: "cursor-pointer",
    },
    {
      field: "patientProgram",
      headerName: "Program",
      width: 170,
      minWidth: 120,
      cellClass: "cursor-pointer",
      valueGetter: (params) => params.data.patientProgram || "-",
      sortable: true,
    },
    {
      field: "primaryCoachName",
      headerName: "Primary Coach",
      width: 180,
      minWidth: 150,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "partnerName",
      headerName: "Partner",
      width: 180,
      minWidth: 150,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "marketName",
      headerName: "Market",
      width: 180,
      minWidth: 150,
      cellClass: "cursor-pointer",
      sortable: true,
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 180,
      minWidth: 150,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.data.createdAt),
      cellClass: "cursor-pointer",
      sortable: true,
    },
  ];

  const getFlaggedValues = async () => {
    const response = [
      {
        value: "true",
        label: "Yes",
      },
      {
        value: "false",
        label: "No",
      },
    ];
    return response;
  };

  const getUpcomingApptsValues = async () => {
    const response = [
      {
        value: Constants.UPCOMING_APPTS,
        label: "Yes",
      },
      {
        value: Constants.NO_UPCOMING_APPTS,
        label: "No",
      },
    ];
    return response;
  };

  const handleOffCanvasShow = (comp: any) => {
    if (comp === "addPatient") {
      setOffCanvasTitle("Add Patient");
    }
    setOffCanvasComp(comp);
    setOffCanvasShow(!offCanvasShow);
  };

  const handlerAddPatientForm = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig);
    setShowToast(true);
  };

  const updateCoach = (values: UsersDto[]) => {
    setCoaches(values ? values.map((v) => v.id) : null);
  };

  const updateProgram = (values: Lookup[]) => {
    setProgram(values);
  };

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    return (await patientsApi.patientsControllerGetGridData(request)).data;
  };

  const handleRowClick = (event: RowClickedEvent) => {
    const rowId = event.data.patientId;
    navigate(`/patients/${rowId}/summary`);
  };

  const loadPathwayStatuses = async () => {
    try {
      const { data } = await workflowApi.workflowControllerGetStatesForWorkflow(
        WorkflowConstants.pathway,
      );
      setPathwayStatuses(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadPathwayStatuses();
  }, []);

  useEffect(() => {
    const defaultStatuses = pathwayStatuses.filter((status) =>
      Object.values(PathwaysConstants).includes(status.stateId),
    );

    setStatuses([
      ...defaultStatuses,
      {
        stateId: Constants.NOT_SET.toLowerCase(),
        stateName: Constants.NOT_SET,
      } as WorkflowState,
    ]);
    setInitialStatusIds([
      ...defaultStatuses.map((status) => status.stateId),
      Constants.NOT_SET.toLowerCase(),
    ]);
  }, [pathwayStatuses]);

  useEffect(() => {
    (async function () {
      const response = await lookupApi.lookupControllerFindAll(
        Lookups.PROGRAM_TYPES,
      );
      const defaultProgram = response.data.filter((p) =>
        Object.values(ProgramConstants).includes(p.id),
      );
      if (!(Constants.PROGRAM_FILTER_KEY in pageFilterState)) {
        setProgram([...defaultProgram]);
      }
    })();
  }, []);

  // --- this reads readux and updated filters --- \\
  useEffect(() => {
    if (!Object.keys(pageFilterState).length) return;
    for (const key in pageFilterState) {
      if (key === Constants.COACH_FILTER_KEY) {
        if (pageFilterState[key] === Constants.COACH_ME) {
          setCoaches([...[UserContextHolder.getUser()?.id!]]);
        }
      } else if (key === Constants.UPCOMING_APPTS_FILTER_KEY) {
        setInitialUpcomingAppts([...[pageFilterState[key]]]);
        setUpcomingAppts([...[{ label: "No", value: pageFilterState[key] }]]);
      }
    }
    dispatch(resetFilter());
  }, [pageFilterState]);

  return (
    <>
      <Toaster
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        title={toastConfig.title}
        message={toastConfig.message}
        icon={toastConfig.icon}
      />
      <main className="main">
        <div
          className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
          aria-label="breadcrumb"
        >
          <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/home">
                    <span className="material-symbols-outlined fs-14">
                      home
                    </span>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Patients
                </li>
              </ol>
              <h1 className="h3 m-0">Patients</h1>
            </div>
          </div>
        </div>
        <div className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom">
          <div className="flex-fill d-flex align-items-center">
            <div className="search-box max-w-md-18-75">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                id="searchInput"
                placeholder="Search by Name, Wellinks ID, Phone, or DoB"
                className="form-control search-box__input fw-normal fs-12"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
            {showExportButton && (
              <ExportDataButton
                colDefs={colDefs}
                dataProvider={loadData}
                filters={filters}
              />
            )}
          </div>
          <div className="d-flex flex-wrap align-items-center">
            <GridWorkflowStatusDropdown
              label="Pathway"
              type={WorkflowConstants.pathway}
              onChange={setStatuses}
              initialStatusIds={initialStatusIds}
            />
            <GridUsersDropdown
              label="Primary Coach"
              onChange={updateCoach}
              initialUserIds={coaches ?? []}
            />
            <GridLookupDropdown
              label="Program"
              type={Lookups.PROGRAM_TYPES}
              onChange={updateProgram}
              initialValues={program}
            />
            <GridDropdown
              label="Upcoming Appts."
              dataProvider={getUpcomingApptsValues}
              onChange={setUpcomingAppts}
              keyProvider={(item) => item.value}
              labelProvider={(item) => item.label}
              initialKeys={initialUpcomingAppts}
            />
            <GridDropdown
              label="Flagged"
              keyProvider={(item) => item.value}
              labelProvider={(item) => item.label}
              dataProvider={getFlaggedValues}
              onChange={setFlagged}
            />
          </div>
        </div>
        <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
          <div className="flex-fill">
            <div className="py-4">
              <GenericGrid
                gridId="patients-grid"
                colDefs={colDefs}
                dataProvider={loadData}
                filters={filters}
                key={gridReloadKey}
                onRowClicked={handleRowClick}
              />
            </div>
          </div>
        </div>
        <OffCanvas
          title={offCanvasTitle}
          offCanvasShow={offCanvasShow}
          setOffCanvasShow={setOffCanvasShow}
          offCanvasComp={offCanvasComp}
        >
          {offCanvasComp === "addPatient" && (
            <AddPatient
              onSubmitPatient={handlerAddPatientForm}
              setOffCanvasShow={setOffCanvasShow}
              reloadGrid={reloadGrid}
            />
          )}
        </OffCanvas>
      </main>

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Filter Task
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-6">
                <label
                  htmlFor="searchPatients"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Patient
                </label>
                <input
                  type="text"
                  name="assignedTo"
                  className="form-control"
                  id="searchPatients"
                  placeholder="Search by patient"
                />
              </div>
              <div className="col-6">
                <label
                  htmlFor="priority"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Source
                </label>
                <select
                  id="priority"
                  className="form-select"
                  name="priority"
                  required
                >
                  <option value="">PGI</option>
                  <option value="">Appointment</option>
                  <option value="">Manual</option>
                </select>
              </div>
              <div className="col-6">
                <label
                  htmlFor="TaskType"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Task Type
                </label>
                <input
                  type="text"
                  name="assignedTo"
                  className="form-control"
                  id="TaskType"
                  placeholder="Search by Task Type"
                />
              </div>
              <div className="col-6">
                <label
                  htmlFor="priority"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Priority
                </label>
                <select
                  id="priority"
                  className="form-select"
                  name="priority"
                  required
                >
                  <option value="">Low </option>
                  <option value="">Mediun </option>
                  <option value="">High </option>
                </select>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-6 my-0"
              onClick={() => setModalShow(false)}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-secondary rounded-pill px-6 my-0"
              onClick={() => setModalShow(false)}
            >
              Apply
            </button>
          </Modal.Footer>
        </>
      </Modal>
    </>
  );
};
